import { useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { AddEditTeam } from 'api/Identity/Types/AddUpdateTeam'
import { TeamDto } from 'api/Identity/Types/Team'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchCountriesAction,
  fetchRegionsAction,
  fetchTickmillCompaniesAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import TitleSearch from '../Users/TitleSearch'
import { AddEditTeamForm } from './AddEditTeamForm'
import teamColumns from './teamColumns'

import styles from '../Users/users.table.module.scss'

const Teams = (): JSX.Element => {
  const [teams, setTeams] = useState<TeamDto[]>([])
  const [addTeamModal, setAddTeamModal] = useState<{ team?: TeamDto; visible: boolean }>({
    visible: false,
  })
  const [idToDelete, setIdToDelete] = useState<null | string>(null)
  const [dataState, setDataState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [auditLogModal, setAuditLogModal] = useState<{ visible: boolean; data?: TeamDto }>({
    visible: false,
  })
  const identityApiClient = useApiClient(IdentityApiClient)

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    const resp = await identityApiClient.fetchTeams()
    setTeams(sortData(resp))
    return resp
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, isLoading } = useFetchOne(fetchData)

  const dispatch = useAppDispatch()
  const setToast = useContext(ToastContext)

  const regions = useAppSelector((state) => state.identity.regions)
  const countries = useAppSelector((state) => state.identity.countries)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)

  useEffect(() => {
    if (data) {
      if (isEmpty(regions)) dispatch(fetchRegionsAction(identityApiClient))
      if (isEmpty(countries)) dispatch(fetchCountriesAction(identityApiClient))
      if (isEmpty(tickmillCompanies)) dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
  }, [data, regions, countries, tickmillCompanies]) // eslint-disable-line react-hooks/exhaustive-deps

  const sortData = (teamDtos: TeamDto[]): TeamDto[] => {
    return teamDtos.sort((a: TeamDto, b: TeamDto) => stringComparator(a.name, b.name))
  }

  const handleDeleteConfirmation = (team: TeamDto) => {
    setIdToDelete(team.id)
  }

  const handleDelete = async () => {
    if (idToDelete) {
      await identityApiClient.deleteTeam(idToDelete)
      setIdToDelete(null)
      forceRefresh()
    }
  }

  const openAddTeamModal = () => {
    setAddTeamModal({ visible: true })
  }

  const handleEdit = (team?: TeamDto) => {
    setAddTeamModal({ team, visible: true })
  }

  const handleSearch = (searchText: string) => {
    if (data) {
      const filterTeams = data.filter((team) =>
        team.name.toLowerCase().includes(searchText.toLowerCase())
      )
      setTeams(filterTeams)
    }
  }

  const handlePageChange = (event: GridPageChangeEvent) => {
    setDataState({ skip: event.page.skip, take: event.page.take })
  }

  const onSubmit = async (values: AddEditTeam) => {
    if (!values.id) {
      await identityApiClient.addTeam(values)
    } else {
      await identityApiClient.updateTeam(values.id, values)
    }
    setToast(successToast(`Team ${values.name} ${values.id ? 'updated' : 'created'}`))
    forceRefresh()
    setAddTeamModal({ visible: false })
  }

  const setAuditLog = (team: TeamDto) => {
    setAuditLogModal({ data: team, visible: true })
  }

  if (isLoading) return <Spinner />

  return (
    <>
      {addTeamModal.visible && (
        <AddEditTeamForm
          isOpen={addTeamModal.visible}
          onClose={() => setAddTeamModal({ visible: false })}
          onSubmit={onSubmit}
          team={addTeamModal.team}
          regions={regions}
          tickmillCompanies={tickmillCompanies}
          countries={countries}
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          teamId={auditLogModal.data.id}
        />
      )}

      <Breadcrumbs crumbs={[{ title: 'Teams', link: links.administrationTeams.url }]} />
      <header>
        <TitleSearch placeholder='Enter name' onSearch={handleSearch} className={styles.action} />
      </header>
      <AddButton text='Add team' onClick={openAddTeamModal} data-test='add-team-button' />
      <Grid
        resizable
        data={teams.slice(dataState.skip, dataState.take + dataState.skip)}
        {...dataState}
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={teams.length}
        take={dataState.take}
        skip={dataState.skip}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0 }}
      >
        {teamColumns
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          width={50}
          title=''
          sortable={false}
          cell={(gridCellProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Edit',
                    'data-test': 'edit',
                    onClick: () => handleEdit(gridCellProps.dataItem),
                  },
                  {
                    name: 'Delete',
                    'data-test': 'delete',
                    onClick: () => handleDeleteConfirmation(gridCellProps.dataItem),
                  },
                  {
                    name: 'Audit Log',
                    onClick: () => setAuditLog(gridCellProps.dataItem),
                  },
                ]}
                data-test='more-button'
              />
            </td>
          )}
        />
      </Grid>
      <ConfirmationModal
        contentText='Are you sure to delete the Team?'
        yesButtonText='Yes, delete'
        onYes={handleDelete}
        onCancel={() => setIdToDelete(null)}
        visible={!!idToDelete}
      />
    </>
  )
}

export default Teams
